import { ChoiceGroup, ChoiceOption, MenuCategory, MenuData, MenuItem, LocalizedText } from '../types';

const fallbackId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const generateId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return fallbackId(prefix);
};

const ensureText = (value: any, fallback: string): LocalizedText => {
  if (value && typeof value === 'object' && ('en' in value || 'ar' in value)) {
    return {
      en: typeof value.en === 'string' ? value.en : fallback,
      ...(typeof value.ar === 'string' && value.ar ? { ar: value.ar } : {}),
    };
  }
  if (typeof value === 'string') {
    return { en: value };
  }
  return { en: fallback };
};

const ensureArray = <T,>(value: T[] | undefined | null): T[] => (Array.isArray(value) ? value : []);

const compareSort = <T extends { sortIndex?: number; name?: any; id?: string }>(a: T, b: T) => {
  const ai = Number.isFinite(a.sortIndex) ? Number(a.sortIndex) : Number.MAX_SAFE_INTEGER;
  const bi = Number.isFinite(b.sortIndex) ? Number(b.sortIndex) : Number.MAX_SAFE_INTEGER;
  if (ai !== bi) return ai - bi;
  const an = (a as any)?.name?.en || (a as any)?.name || '';
  const bn = (b as any)?.name?.en || (b as any)?.name || '';
  if (an && bn && an.toString().localeCompare) {
    const cmp = an.toString().localeCompare(bn.toString());
    if (cmp !== 0) return cmp;
  }
  return (a.id || '').localeCompare(b.id || '');
};

const normalizeChoiceOption = (option: Partial<ChoiceOption> | undefined, idx: number): ChoiceOption => ({
  id: option?.id || generateId('opt'),
  name: ensureText(option?.name, `Option ${idx + 1}`),
  priceModifier: Number.isFinite(option?.priceModifier) ? Number(option?.priceModifier) : 0,
  sortIndex: Number.isFinite(option?.sortIndex) ? Number(option?.sortIndex) : idx,
});

export const normalizeChoiceGroup = (group: Partial<ChoiceGroup> | undefined, idx: number): ChoiceGroup => {
  const opts = ensureArray(group?.options).map(normalizeChoiceOption).sort(compareSort);
  return {
    id: group?.id || generateId('cg'),
    name: ensureText(group?.name, `Group ${idx + 1}`),
    options: opts,
    required: Boolean(group?.required),
    optional: group?.optional ?? !group?.required,
    min: typeof group?.min === 'number' && Number.isFinite(group.min) ? group.min : 0,
    max: typeof group?.max === 'number' && Number.isFinite(group.max) ? group.max : 0,
    sortIndex: Number.isFinite(group?.sortIndex) ? Number(group?.sortIndex) : idx,
  };
};

export const normalizeMenuItem = (item: Partial<MenuItem> | undefined, idx: number): MenuItem => {
  const groups = ensureArray(item?.choiceGroups).map(normalizeChoiceGroup).sort(compareSort);
  return {
    id: item?.id || generateId('item'),
    name: ensureText(item?.name, `Item ${idx + 1}`),
    description: ensureText(item?.description, ''),
    price: typeof item?.price === 'number' && Number.isFinite(item.price) ? item.price : Number(item?.price || 0),
    image: typeof item?.image === 'string' && item.image.trim() ? item.image : undefined,
    choiceGroups: groups,
  };
};

export const normalizeMenuCategory = (category: Partial<MenuCategory> | undefined, idx: number): MenuCategory => ({
  id: category?.id || generateId('cat'),
  name: ensureText(category?.name, `Section ${idx + 1}`),
  items: ensureArray(category?.items).map(normalizeMenuItem),
});

export const normalizeMenuData = (menu: Partial<MenuData> | null | undefined): MenuData => ({
  id: menu?.id,
  title: ensureText(menu?.title, 'New Menu'),
  categories: ensureArray(menu?.categories).map(normalizeMenuCategory),
});

export const summarizeMenu = (menu: MenuData) => {
  const categories = menu.categories?.length || 0;
  const items = menu.categories?.reduce((acc, cat) => acc + (cat.items?.length || 0), 0) || 0;
  return { categories, items, title: menu.title?.en || 'Menu' };
};
